/**
 * Workforce Training Curricula Page
 *
 * Manages training programs and curriculum enrollment including:
 * - Curriculum definitions with modules
 * - User enrollment tracking
 * - Progress monitoring
 * - Completion certificates
 */

import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Plus,
  Search,
  Users,
  Clock,
  ChevronRight,
  X,
  Edit2,
  Play,
  FileText,
  GraduationCap,
  Target,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { Link } from 'react-router-dom';
import { useCapability } from '@/hooks/useCapabilities';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface CurriculumModule {
  id: number;
  title: string;
  description: string;
  sequence_order: number;
  duration_minutes: number;
  content_type: string;
  required: boolean;
}

interface Curriculum {
  id: number;
  name: string;
  description: string;
  target_role: string;
  duration_hours: number;
  modules_count: number;
  enrolled_count: number;
  completed_count: number;
  status: string;
  created_at: string;
  modules?: CurriculumModule[];
  // Raw workforce_curricula fields (preserved by normalizeCurriculum's spread)
  // used to prefill the edit form.
  curriculum_type?: string;
  total_hours?: number;
  has_final_exam?: boolean;
  passing_score?: number;
  self_paced?: boolean;
  target_roles?: string[];
  // Linked LMS classroom course — the delivery layer where the actual
  // modules/lessons live (e.g. the Defensoras Cohort course).
  lms_course_id?: number | null;
  lms_course_title?: string | null;
  lms_course_published?: boolean | null;
  lms_module_count?: number;
  lms_lesson_count?: number;
  lms_enrolled_count?: number;
  linked_courses?: { id: number; title: string; is_published: boolean }[];
}

// The API returns raw workforce_curricula fields (is_published, total_hours,
// enrollment_count, …) — normalize them onto the card-friendly shape above.
// When a curriculum is delivered through a linked LMS course, module/enrolled
// counts come from the course (the curriculum's own modules JSONB is usually
// an empty shell).
const normalizeCurriculum = (r: any): Curriculum => ({
  ...r,
  status: r.status ?? (r.is_published ? 'active' : 'draft'),
  target_role: r.target_role ?? (Array.isArray(r.target_roles) ? r.target_roles[0] : ''),
  duration_hours: r.duration_hours ?? r.total_hours ?? 0,
  modules_count:
    r.modules_count ??
    (r.lms_course_id ? r.lms_module_count || 0 : Array.isArray(r.modules) ? r.modules.length : 0),
  enrolled_count: r.enrolled_count ?? (r.lms_course_id ? r.lms_enrolled_count || 0 : r.enrollment_count || 0),
  completed_count: r.completed_count ?? r.completion_count ?? 0,
});

interface Enrollment {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  curriculum_id: number;
  curriculum_name: string;
  status: string;
  progress_percentage: number;
  enrolled_at: string;
  completed_at: string | null;
  current_module: string | null;
}

// A workforce roster member — the pool of learners who can be enrolled into a
// curriculum. Shape mirrors /api/workforce/roster (RosterEntryResponse).
interface RosterUser {
  user_id: number;
  user_name: string;
  role_name?: string | null;
  status?: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-paper-2 text-ink-700',
  active: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-amber-100 text-amber-700',
};

interface NewCurriculumForm {
  name: string;
  description: string;
  curriculum_type: string;
  total_hours: number;
  target_roles: string[];
  has_final_exam: boolean;
  passing_score: number;
  self_paced: boolean;
}

const initialCurriculumForm: NewCurriculumForm = {
  name: '',
  description: '',
  curriculum_type: 'onboarding',
  total_hours: 8,
  target_roles: [],
  has_final_exam: false,
  passing_score: 80,
  self_paced: true,
};

export default function WorkforceCurriculaPage() {
  const { t } = useAppTranslation();
  const _orgId = Number(localStorage.getItem('active_cbo_id')) || null;
  const { enabled: lmsEnabled } = useCapability(_orgId, 'lms');
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurriculum, setSelectedCurriculum] = useState<Curriculum | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [editingCurriculum, setEditingCurriculum] = useState<Curriculum | null>(null);
  const [newCurriculum, setNewCurriculum] = useState<NewCurriculumForm>(initialCurriculumForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Enroll Learners modal state. The footer button set showEnrollModal but no
  // modal ever consumed it, so clicking it did nothing (TRUS-407).
  const [rosterUsers, setRosterUsers] = useState<RosterUser[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [enrollSearch, setEnrollSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for curricula page');
      setLoading(false);
      return;
    }

    try {

      const [curriculaRes, enrollmentsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/curricula')),
        authFetch(getWeaveinApiUrl('/api/workforce/curricula/enrollments')),
      ]);

      if (curriculaRes.ok) {
        const data = await curriculaRes.json();
        setCurricula((data.items || []).map(normalizeCurriculum));
      }

      if (enrollmentsRes.ok) {
        const data = await enrollmentsRes.json();
        setEnrollments(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching curricula:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurriculumDetails = async (id: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/curricula/${id}`));

      if (res.ok) {
        const data = await res.json();
        setSelectedCurriculum(normalizeCurriculum(data));
      }
    } catch (error) {
      console.error('Error fetching curriculum details:', error);
    }
  };

  const handleSelectCurriculum = (curriculum: Curriculum) => {
    fetchCurriculumDetails(curriculum.id);
  };

  // Open the modal prefilled from an existing curriculum (edit) or blank (create).
  const openCurriculumModal = (curriculum: Curriculum | null) => {
    setEditingCurriculum(curriculum);
    setSubmitError(null);
    setNewCurriculum(
      curriculum
        ? {
            name: curriculum.name || '',
            description: curriculum.description || '',
            curriculum_type: curriculum.curriculum_type || 'onboarding',
            total_hours: curriculum.total_hours ?? curriculum.duration_hours ?? 8,
            target_roles: curriculum.target_roles || [],
            has_final_exam: curriculum.has_final_exam ?? false,
            passing_score: curriculum.passing_score ?? 80,
            self_paced: curriculum.self_paced ?? true,
          }
        : initialCurriculumForm,
    );
    setShowAddModal(true);
  };

  const closeCurriculumModal = () => {
    setShowAddModal(false);
    setEditingCurriculum(null);
    setNewCurriculum(initialCurriculumForm);
  };

  const handleSaveCurriculum = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = getAuthToken();
      // Edit updates the fields in the form only (modules & links untouched);
      // create seeds a starter module so the curriculum isn't an empty shell.
      const response = editingCurriculum
        ? await authFetch(getWeaveinApiUrl(`/api/workforce/curricula/${editingCurriculum.id}`), {
            method: 'PUT',
            jsonBody: { ...newCurriculum },
          })
        : await authFetch(getWeaveinApiUrl('/api/workforce/curricula'), {
            method: 'POST',
            jsonBody: {
              ...newCurriculum,
              modules: [{ order: 1, title: 'Introduction', description: 'Introduction module', is_required: true, estimated_hours: 1 }],
            },
          });

      if (response.ok) {
        closeCurriculumModal();
        fetchData();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to create curriculum';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error creating curriculum:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open the Enroll Learners modal and load the roster to pick from.
  const openEnrollModal = async () => {
    setShowEnrollModal(true);
    setSelectedUserIds([]);
    setEnrollSearch('');
    setEnrollError(null);
    setRosterLoading(true);
    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/roster?limit=200'));
      if (res.ok) {
        const data = await res.json();
        setRosterUsers(data.items || []);
      } else {
        setEnrollError('Could not load the roster. Please try again.');
      }
    } catch {
      setEnrollError('Network error loading the roster.');
    } finally {
      setRosterLoading(false);
    }
  };

  const closeEnrollModal = () => {
    setShowEnrollModal(false);
    setSelectedUserIds([]);
    setEnrollError(null);
  };

  const toggleEnrollUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  // Enroll each selected learner via the curriculum enroll endpoint. A learner
  // already enrolled (HTTP 400) is treated as a no-op rather than failing the
  // whole batch, so re-enrolling a mixed selection still succeeds.
  const handleEnrollLearners = async () => {
    if (!selectedCurriculum || selectedUserIds.length === 0) return;
    setEnrolling(true);
    setEnrollError(null);
    const failures: string[] = [];
    for (const userId of selectedUserIds) {
      try {
        const res = await authFetch(
          getWeaveinApiUrl(`/api/workforce/curricula/${selectedCurriculum.id}/enroll`),
          { method: 'POST', jsonBody: { user_id: userId, curriculum_id: selectedCurriculum.id } },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = typeof err.detail === 'string' ? err.detail : '';
          // "already enrolled" is a benign no-op, not a failure.
          if (!/already enrolled/i.test(detail)) {
            const name = rosterUsers.find((u) => u.user_id === userId)?.user_name || `User ${userId}`;
            failures.push(`${name}: ${detail || `error ${res.status}`}`);
          }
        }
      } catch {
        const name = rosterUsers.find((u) => u.user_id === userId)?.user_name || `User ${userId}`;
        failures.push(`${name}: network error`);
      }
    }
    setEnrolling(false);
    if (failures.length > 0) {
      setEnrollError(failures.join('; '));
      return;
    }
    closeEnrollModal();
    // Refresh enrollments + counts and reload the open curriculum's detail.
    fetchData();
    if (selectedCurriculum) fetchCurriculumDetails(selectedCurriculum.id);
  };

  const filteredCurricula = curricula.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.target_role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalCurricula: curricula.length,
    activeCurricula: curricula.filter(c => c.status === 'active').length,
    totalEnrolled: enrollments.filter(e => e.status !== 'dropped').length,
    completedThisMonth: enrollments.filter(e => {
      if (!e.completed_at) return false;
      const completedDate = new Date(e.completed_at);
      const now = new Date();
      return completedDate.getMonth() === now.getMonth() && completedDate.getFullYear() === now.getFullYear();
    }).length,
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Training"
        title={t('workforce.curricula.title', 'Training Curricula')}
        description={t('workforce.curricula.description', 'Manage training programs and track learner progress')}
        actions={
          <>
            <button
              onClick={() => openCurriculumModal(null)}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              <span>{t('workforce.curricula.createCurriculum', 'Create Curriculum')}</span>
            </button>
          </>
        }
      />
      <PageContainer>
      <div className="space-y-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.curricula.stats.totalCurricula', 'Total Curricula')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalCurricula}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.curricula.stats.activePrograms', 'Active Programs')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.activeCurricula}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.curricula.stats.totalEnrolled', 'Total Enrolled')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalEnrolled}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <GraduationCap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.curricula.stats.completedMonth', 'Completed (Month)')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.completedThisMonth}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search. (The old Enrollments sub-view was removed: per-learner
            session tracking lives in Training → Schedule & Attendance, and
            per-curriculum enrollment shows in the detail panel below.) */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-end">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              type="text"
              placeholder={t('workforce.curricula.searchCurricula', 'Search curricula...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Curricula Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full py-12 text-center text-ink-500">
                {t('workforce.curricula.loading', 'Loading curricula...')}
              </div>
            ) : filteredCurricula.length === 0 ? (
              <div className="col-span-full py-12 text-center">
                <BookOpen className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                <p className="text-ink-500">{t('workforce.curricula.noCurricula', 'No curricula found')}</p>
                <button
                  onClick={() => openCurriculumModal(null)}
                  className="text-emerald-600 hover:text-emerald-700 font-medium mt-2"
                >
                  {t('workforce.curricula.createFirst', 'Create your first curriculum')}
                </button>
              </div>
            ) : (
              filteredCurricula.map((curriculum) => (
                <div
                  key={curriculum.id}
                  className="bg-surface rounded-xl border border-v2border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleSelectCurriculum(curriculum)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <BookOpen className="w-6 h-6 text-emerald-600" />
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[curriculum.status]}`}>
                        {curriculum.status}
                      </span>
                    </div>
                    <h3 className="font-semibold text-ink mb-1">{curriculum.name}</h3>
                    <p className="text-sm text-ink-500 line-clamp-2 mb-4">{curriculum.description}</p>

                    <div className="mb-4 flex flex-wrap items-center gap-1.5">
                      {curriculum.curriculum_type && (
                        <span className="text-xs bg-paper-2 text-ink-500 px-2 py-1 rounded capitalize">
                          {curriculum.curriculum_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      {curriculum.target_role && (
                        <span className="text-xs bg-paper-2 text-ink-500 px-2 py-1 rounded">
                          For: {curriculum.target_role}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center border-t border-v2border-muted pt-4">
                      <div>
                        <p className="text-lg font-semibold text-ink">{curriculum.modules_count || 0}</p>
                        <p className="text-xs text-ink-500">{t('workforce.curricula.card.modules', 'Modules')}</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-ink">{curriculum.duration_hours || 0}h</p>
                        <p className="text-xs text-ink-500">{t('workforce.curricula.card.duration', 'Duration')}</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-ink">{curriculum.enrolled_count || 0}</p>
                        <p className="text-xs text-ink-500">{t('workforce.curricula.card.enrolled', 'Enrolled')}</p>
                      </div>
                    </div>

                    {/* Delivery layer: the linked LMS classroom course */}
                    {lmsEnabled && curriculum.lms_course_id && (
                      <div
                        className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-v2border-muted bg-paper px-3 py-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="min-w-0">
                          <p className="text-[11px] uppercase tracking-wide text-ink-400">
                            {t('workforce.curricula.card.classroomCourse', 'Classroom course')}
                          </p>
                          <p className="text-sm font-medium text-ink truncate">{curriculum.lms_course_title}</p>
                          <p className="text-xs text-ink-500">
                            {curriculum.lms_module_count || 0} {t('workforce.curricula.card.modules', 'Modules').toLowerCase()} ·{' '}
                            {curriculum.lms_lesson_count || 0} {t('workforce.curricula.card.lessons', 'lessons')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Link
                            to={`/workforce/learn/${curriculum.lms_course_id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                          >
                            <Play className="w-3 h-3" />
                            {t('workforce.curricula.card.startTraining', 'Start training')}
                          </Link>
                          <Link
                            to={`/workforce/courses/${curriculum.lms_course_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-v2border px-2.5 py-1 text-xs font-medium text-ink hover:bg-paper-2"
                          >
                            <GraduationCap className="w-3 h-3" />
                            {t('workforce.curricula.card.editCourse', 'Edit course')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* All cohort courses linked to this curriculum (reverse
                        lookup) — a curriculum can have several cohort deliveries. */}
                    {lmsEnabled && (curriculum.linked_courses?.length ?? 0) > 0 && (
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <p className="text-[11px] uppercase tracking-wide text-ink-400 mb-1">
                          {t('workforce.curricula.card.cohortCourses', 'Cohort courses')}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {curriculum.linked_courses!.map((lc) => (
                            <Link
                              key={lc.id}
                              to={`/workforce/courses/${lc.id}`}
                              className="inline-flex items-center gap-1 rounded-full border border-v2border px-2.5 py-1 text-xs text-ink hover:bg-paper-2"
                            >
                              <GraduationCap className="w-3 h-3" />
                              {lc.title}
                              {!lc.is_published && <span className="text-ink-400">(draft)</span>}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="px-6 py-3 bg-paper border-t border-v2border-muted flex items-center justify-between">
                    <span className="text-sm text-emerald-600 font-medium">
                      {curriculum.completed_count || 0} completed
                    </span>
                    <ChevronRight className="w-4 h-4 text-ink-400" />
                  </div>
                </div>
              ))
            )}
          </div>

        {/* Curriculum Detail Slide-out */}
        {selectedCurriculum && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCurriculum(null)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-surface shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">{selectedCurriculum.name}</h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        openCurriculumModal(selectedCurriculum);
                        setSelectedCurriculum(null);
                      }}
                      className="p-2 hover:bg-paper-2 rounded"
                    >
                      <Edit2 className="w-5 h-5 text-ink-500" />
                    </button>
                    <button
                      onClick={() => setSelectedCurriculum(null)}
                      className="p-2 hover:bg-paper-2 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Overview */}
                  <div>
                    <p className="text-ink-500">{selectedCurriculum.description}</p>
                    <div className="flex items-center space-x-4 mt-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedCurriculum.status]}`}>
                        {selectedCurriculum.status}
                      </span>
                      {selectedCurriculum.curriculum_type && (
                        <span className="text-sm text-ink-500">
                          Type:{' '}
                          <span className="font-medium text-ink-700 capitalize">
                            {selectedCurriculum.curriculum_type.replace(/_/g, ' ')}
                          </span>
                        </span>
                      )}
                      {selectedCurriculum.target_role && (
                        <span className="text-sm text-ink-500">
                          Target Role: <span className="font-medium text-ink-700">{selectedCurriculum.target_role}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4 bg-paper rounded-xl p-4">
                    <div className="text-center">
                      <p className="font-display text-2xl font-semibold text-ink">{selectedCurriculum.modules_count || 0}</p>
                      <p className="text-sm text-ink-500">Modules</p>
                    </div>
                    <div className="text-center">
                      <p className="font-display text-2xl font-semibold text-ink">{selectedCurriculum.duration_hours || 0}h</p>
                      <p className="text-sm text-ink-500">Duration</p>
                    </div>
                    <div className="text-center">
                      <p className="font-display text-2xl font-semibold text-ink">{selectedCurriculum.enrolled_count || 0}</p>
                      <p className="text-sm text-ink-500">Enrolled</p>
                    </div>
                    <div className="text-center">
                      <p className="font-display text-2xl font-semibold text-emerald-600">{selectedCurriculum.completed_count || 0}</p>
                      <p className="text-sm text-ink-500">Completed</p>
                    </div>
                  </div>

                  {/* Modules */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-ink">Curriculum Modules</h3>
                      <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                        + Add Module
                      </button>
                    </div>
                    {selectedCurriculum.modules && selectedCurriculum.modules.length > 0 ? (
                      <div className="space-y-3">
                        {selectedCurriculum.modules.map((module, index) => (
                          <div
                            key={module.id}
                            className="flex items-center space-x-4 p-4 bg-surface border border-v2border rounded-lg"
                          >
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-ink">{module.title}</p>
                              <p className="text-sm text-ink-500">{module.description}</p>
                              <div className="flex items-center space-x-4 mt-2">
                                <span className="text-xs text-ink-400 flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {module.duration_minutes} min
                                </span>
                                <span className="text-xs text-ink-400 flex items-center">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {module.content_type}
                                </span>
                                {module.required && (
                                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                                    Required
                                  </span>
                                )}
                              </div>
                            </div>
                            <button className="p-2 hover:bg-paper-2 rounded">
                              <Play className="w-4 h-4 text-ink-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : selectedCurriculum.lms_course_id ? (
                      <div className="text-center py-8 bg-paper rounded-xl">
                        <GraduationCap className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                        <p className="text-ink-700 font-medium">{selectedCurriculum.lms_course_title}</p>
                        <p className="text-sm text-ink-500 mt-1">
                          Content is delivered through this classroom course —{' '}
                          {selectedCurriculum.lms_module_count || 0} modules,{' '}
                          {selectedCurriculum.lms_lesson_count || 0} lessons.
                        </p>
                        <div className="mt-3 flex items-center justify-center gap-2">
                          <Link
                            to={`/workforce/learn/${selectedCurriculum.lms_course_id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
                          >
                            <Play className="w-3.5 h-3.5" /> Start training
                          </Link>
                          <Link
                            to={`/workforce/courses/${selectedCurriculum.lms_course_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-v2border px-3 py-1.5 text-sm font-medium text-ink hover:bg-paper-2"
                          >
                            Edit course
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-paper rounded-xl">
                        <FileText className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No modules added yet</p>
                        <button className="text-emerald-600 hover:text-emerald-700 font-medium mt-2 text-sm">
                          Add your first module
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Recent Enrollments */}
                  <div>
                    <h3 className="font-semibold text-ink mb-4">Recent Enrollments</h3>
                    {enrollments.filter(e => e.curriculum_id === selectedCurriculum.id).length > 0 ? (
                      <div className="space-y-2">
                        {enrollments
                          .filter(e => e.curriculum_id === selectedCurriculum.id)
                          .slice(0, 5)
                          .map((enrollment) => (
                            <div
                              key={enrollment.id}
                              className="flex items-center justify-between p-3 bg-paper rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-paper-2 flex items-center justify-center">
                                  <span className="text-ink-500 text-sm font-medium">
                                    {enrollment.user_name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <span className="font-medium text-ink">{enrollment.user_name}</span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="w-20 h-1.5 bg-paper-2 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500 rounded-full"
                                    style={{ width: `${enrollment.progress_percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-ink-500">{enrollment.progress_percentage}%</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-sm text-ink-500">No enrollments yet</p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-v2border flex items-center justify-between">
                  <button
                    onClick={openEnrollModal}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Enroll Learners</span>
                  </button>
                  <button className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg">
                    View All Enrollments
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Curriculum Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={closeCurriculumModal} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-ink">{editingCurriculum ? 'Edit Curriculum' : 'Create Curriculum'}</h2>
                  <button
                    onClick={closeCurriculumModal}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSaveCurriculum} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Curriculum Name *</label>
                    <input
                      type="text"
                      required
                      value={newCurriculum.name}
                      onChange={(e) => setNewCurriculum({ ...newCurriculum, name: e.target.value })}
                      placeholder="e.g., CHW Core Training"
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                    <textarea
                      value={newCurriculum.description}
                      onChange={(e) => setNewCurriculum({ ...newCurriculum, description: e.target.value })}
                      placeholder="Describe this curriculum..."
                      rows={3}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Type</label>
                      <select
                        value={newCurriculum.curriculum_type}
                        onChange={(e) => setNewCurriculum({ ...newCurriculum, curriculum_type: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="onboarding">Onboarding</option>
                        <option value="chw_certification">CHW Certification</option>
                        <option value="haa_certification">HAA Certification</option>
                        <option value="continuing_education">Continuing Education</option>
                        <option value="mentoring">Mentoring</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Total Hours</label>
                      <input
                        type="number"
                        min={1}
                        max={500}
                        value={newCurriculum.total_hours}
                        onChange={(e) => setNewCurriculum({ ...newCurriculum, total_hours: parseInt(e.target.value) || 8 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newCurriculum.self_paced}
                        onChange={(e) => setNewCurriculum({ ...newCurriculum, self_paced: e.target.checked })}
                        className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">Self-paced</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newCurriculum.has_final_exam}
                        onChange={(e) => setNewCurriculum({ ...newCurriculum, has_final_exam: e.target.checked })}
                        className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">Has final exam</span>
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeCurriculumModal}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {submitting ? 'Saving...' : editingCurriculum ? 'Save Changes' : 'Create Curriculum'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Enroll Learners Modal (TRUS-407) */}
        {showEnrollModal && selectedCurriculum && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={closeEnrollModal} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-lg font-semibold text-ink">Enroll Learners</h2>
                  <button onClick={closeEnrollModal} className="p-2 hover:bg-paper-2 rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-ink-500 mb-4">{selectedCurriculum.name}</p>

                {enrollError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{enrollError}</div>
                )}

                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input
                    type="text"
                    placeholder="Search learners..."
                    value={enrollSearch}
                    onChange={(e) => setEnrollSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div className="max-h-72 overflow-y-auto border border-v2border rounded-lg divide-y divide-v2border-muted">
                  {rosterLoading ? (
                    <div className="py-8 text-center text-ink-500 text-sm">Loading roster...</div>
                  ) : (
                    (() => {
                      const term = enrollSearch.toLowerCase();
                      const visible = rosterUsers.filter(
                        (u) =>
                          u.user_name?.toLowerCase().includes(term) ||
                          (u.role_name || '').toLowerCase().includes(term),
                      );
                      if (visible.length === 0) {
                        return (
                          <div className="py-8 text-center text-ink-500 text-sm">
                            <Users className="w-8 h-8 text-ink-400 mx-auto mb-2" />
                            {rosterUsers.length === 0 ? 'No roster members found' : 'No learners match your search'}
                          </div>
                        );
                      }
                      return visible.map((u) => (
                        <label
                          key={u.user_id}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-paper-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.user_id)}
                            onChange={() => toggleEnrollUser(u.user_id)}
                            className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink truncate">{u.user_name}</p>
                            {u.role_name && <p className="text-xs text-ink-500 truncate">{u.role_name}</p>}
                          </div>
                        </label>
                      ));
                    })()
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeEnrollModal}
                    className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEnrollLearners}
                    disabled={enrolling || selectedUserIds.length === 0}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                  >
                    {enrolling
                      ? 'Enrolling...'
                      : selectedUserIds.length > 0
                        ? `Enroll ${selectedUserIds.length}`
                        : 'Enroll'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </PageContainer>
    </div>
  );
}
